import React, { useState } from 'react';
import {
    Table,
    Tag,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    Space,
    Typography,
    Popconfirm,
    Tooltip,
    Spin,
    Alert,
    message,
    Divider,
    Card,
    Row,
    Col,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    StopOutlined,
    GlobalOutlined,
    HomeOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    listDirectory,
    createContact,
    updateContact,
    archiveContact,
    deleteContact,
} from '../../config/services/patrolService';
import { getAllProperties } from '../../config/getApis';
import { parseApiError, bindFormErrors } from '../../config/services/errorHandler';
import { useSelector } from 'react-redux';
import useColors from '../../hooks/useColors';

const { Text } = Typography;
const { Option } = Select;

/**
 * ContactDirectoryPage
 * Route: /contact-directory
 */
const ContactDirectoryPage = () => {
    const { colorPrimary } = useColors();
    const queryClient = useQueryClient();
    const user = useSelector((state) => state.AppReducer?.user);
    const isRole = user?.userRole?.[0];
    const canWrite = isRole === 'admin' || isRole === 'manager';

    const [modalState, setModalState] = useState({ open: false, contact: null });
    const [form] = Form.useForm();
    const scopeValue = Form.useWatch('scope', form);

    const { data: contacts = [], isLoading, refetch } = useQuery(
        ['directory'],
        () => listDirectory(),
        {
            onError: (err) => message.error(parseApiError(err).message),
        }
    );

    const { data: properties = [] } = useQuery('allProperties', getAllProperties);

    const propertyOptions = properties.map((p) => ({ label: p.name, value: p.id }));

    // ── Mutations ──────────────────────────────────────────────
    const saveMutation = useMutation(
        (body) => {
            const { id, ...data } = body;
            return id ? updateContact(id, data) : createContact(data);
        },
        {
            onSuccess: () => {
                message.success(modalState.contact ? 'Contact updated.' : 'Contact created.');
                queryClient.invalidateQueries(['directory']);
                closeModal();
            },
            onError: (err) => {
                const parsed = parseApiError(err);
                if (parsed.fields) bindFormErrors(form, parsed.fields);
                else message.error(parsed.message);
            },
        }
    );

    const archiveMutation = useMutation(
        (id) => archiveContact(id),
        {
            onSuccess: () => {
                message.success('Contact archived.');
                queryClient.invalidateQueries(['directory']);
            },
            onError: (err) => message.error(parseApiError(err).message),
        }
    );

    const deleteMutation = useMutation(
        (id) => deleteContact(id),
        {
            onSuccess: () => {
                message.success('Contact permanently deleted.');
                queryClient.invalidateQueries(['directory']);
            },
            onError: (err) => message.error(parseApiError(err).message),
        }
    );

    const openCreate = () => {
        setModalState({ open: true, contact: null });
        form.resetFields();
        form.setFieldsValue({ scope: 'site', isActive: true });
    };

    const openEdit = (contact) => {
        setModalState({ open: true, contact });
        form.setFieldsValue({
            scope: contact.scope,
            fullName: contact.fullName,
            jobTitle: contact.jobTitle ?? '',
            mobileNumber: contact.mobileNumber ?? '',
            email: contact.email ?? '',
            propertyIds: contact.properties?.map((p) => p.id) ?? [],
            isActive: contact.isActive ?? true,
        });
    };

    const closeModal = () => {
        setModalState({ open: false, contact: null });
        form.resetFields();
    };

    const handleSave = (values) => {
        // When scope = global, backend removes all property links — don't send propertyIds
        const payload = {
            ...(modalState.contact ? { id: modalState.contact.id } : {}),
            scope: values.scope,
            fullName: values.fullName,
            jobTitle: values.jobTitle || null,
            mobileNumber: values.mobileNumber || null,
            email: values.email || null,
            isActive: values.isActive ?? true,
            ...(values.scope === 'site' ? { propertyIds: values.propertyIds ?? [] } : {}),
        };
        saveMutation.mutate(payload);
    };

    const columns = [
        {
            title: 'Name',
            key: 'name',
            width: 200,
            render: (_, r) => (
                <Space size={6} style={{ maxWidth: '100%' }}>
                    {r.scope === 'global' ? (
                        <GlobalOutlined style={{ color: '#6366f1', flexShrink: 0 }} title="Global" />
                    ) : (
                        <HomeOutlined style={{ color: '#10b981', flexShrink: 0 }} title="Site-specific" />
                    )}
                    <div style={{ minWidth: 0 }}>
                        <Text strong ellipsis={{ tooltip: r.fullName }} style={{ display: 'block', maxWidth: 160 }}>
                            {r.fullName}
                        </Text>
                        {r.jobTitle && (
                            <Text
                                type="secondary"
                                ellipsis={{ tooltip: r.jobTitle }}
                                style={{ display: 'block', maxWidth: 160, fontSize: 11 }}
                            >
                                {r.jobTitle}
                            </Text>
                        )}
                    </div>
                </Space>
            ),
        },
        {
            title: 'Scope',
            dataIndex: 'scope',
            key: 'scope',
            width: 100,
            render: (s) => <Tag color={s === 'global' ? 'purple' : 'cyan'}>{s?.toUpperCase()}</Tag>,
        },
        {
            title: 'Properties',
            key: 'properties',
            width: 220,
            render: (_, r) => {
                const properties = r.properties ?? [];
                if (properties.length === 0) return <Text type="secondary">—</Text>;
                const visible = properties.slice(0, 2);
                const rest = properties.slice(2);
                return (
                    <Space size={4} wrap style={{ maxWidth: 200 }}>
                        {visible.map((p) => <Tag key={p.id} style={{ fontSize: 10 }}>{p.name}</Tag>)}
                        {rest.length > 0 && (
                            <Tooltip
                                title={rest.map((p) => p.name).join(', ')}
                                overlayStyle={{ maxWidth: 480 }}
                            >
                                <Tag style={{ fontSize: 10, cursor: 'default' }}>+{rest.length} more</Tag>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Mobile',
            dataIndex: 'mobileNumber',
            key: 'mobile',
            width: 140,
            render: (m) => m || '—',
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 200,
            ellipsis: { showTitle: true },
            render: (e) => e || '—',
        },
        {
            title: 'Status',
            key: 'status',
            width: 130,
            render: (_, r) => (
                <Space size={4}>
                    {r.isActive ? <Tag color="green">Active</Tag> : <Tag color="default">Inactive</Tag>}
                    {r.archivedAt && <Tag color="red">Archived</Tag>}
                </Space>
            ),
        },
        ...(canWrite
            ? [
                {
                    title: 'Actions',
                    key: 'actions',
                    width: 130,
                    align: 'right',
                    render: (_, r) => (
                        <Space>
                            <Tooltip title="Edit">
                                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                            </Tooltip>
                            <Popconfirm
                                title="Archive contact?"
                                description="Contact will be made inactive. This is the recommended action."
                                okText="Archive"
                                onConfirm={() => archiveMutation.mutate(r.id)}
                            >
                                <Tooltip title="Archive (recommended)">
                                    <Button size="small" icon={<StopOutlined />} />
                                </Tooltip>
                            </Popconfirm>
                            {isRole === 'admin' && (
                                <Popconfirm
                                    title="Permanently delete this contact?"
                                    description="This cannot be undone. Archive is recommended instead."
                                    okText="Delete Permanently"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => deleteMutation.mutate(r.id)}
                                >
                                    <Tooltip title="Permanent delete (admin only)">
                                        <Button size="small" icon={<DeleteOutlined />} danger />
                                    </Tooltip>
                                </Popconfirm>
                            )}
                        </Space>
                    ),
                },
            ]
            : []),
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <Text style={{ fontSize: 20, fontWeight: 700 }}>Contact Directory</Text>
                    <Text type="secondary" style={{ marginLeft: 10, fontSize: 13 }}>
                        Global &amp; site-specific contacts
                    </Text>
                </div>
                <Space size={10}>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={refetch}
                        style={{
                            height: '40px',
                            borderRadius: '8px',
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            borderColor: '#d9d9d9',
                            boxShadow: 'none',
                        }}
                    >
                        Refresh
                    </Button>
                    {canWrite && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined style={{ color: '#fff' }} />}
                            onClick={openCreate}
                            style={{
                                backgroundColor: colorPrimary,
                                color: '#ffffff',
                                height: '40px',
                                borderRadius: '8px',
                                border: 'none',
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                paddingLeft: '20px',
                                paddingRight: '20px',
                                boxShadow: 'none',
                            }}
                        >
                            Add Contact
                        </Button>
                    )}
                </Space>
            </div>

            <Spin spinning={isLoading}>
                <Table
                    dataSource={contacts}
                    columns={columns}
                    rowKey="id"
                    size="small"
                    tableLayout="fixed"
                    scroll={{ x: 1120 }}
                    pagination={{ pageSize: 20, hideOnSinglePage: true }}
                />
            </Spin>

            {/* Create / Edit Modal */}
            <Modal
                title={modalState.contact ? `Edit — ${modalState.contact.fullName}` : 'Add Contact'}
                open={modalState.open}
                onCancel={closeModal}
                footer={null}
                width={560}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSave} autoComplete="off">
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item label="Scope" name="scope" rules={[{ required: true }]}>
                                <Select
                                    onChange={(val) => {
                                        if (val === 'global') {
                                            form.setFieldValue('propertyIds', []);
                                        }
                                    }}
                                >
                                    <Option value="global">Global (all properties)</Option>
                                    <Option value="site">Site-specific</Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={14}>
                            <Form.Item
                                label="Full Name"
                                name="fullName"
                                rules={[
                                    { required: true, message: 'Full name is required.' },
                                    { max: 255, message: 'Max 255 characters.' },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={10}>
                            <Form.Item
                                label="Job Title"
                                name="jobTitle"
                                rules={[{ max: 255, message: 'Max 255 characters.' }]}
                            >
                                <Input placeholder="Optional" />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Mobile Number"
                                name="mobileNumber"
                                rules={[
                                    { max: 40, message: 'Max 40 characters.' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value && !getFieldValue('email')) {
                                                return Promise.reject('At least one of mobile or email is required.');
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <Input placeholder="+15550100" />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Email"
                                name="email"
                                rules={[
                                    { type: 'email', message: 'Enter a valid email.' },
                                    { max: 255, message: 'Max 255 characters.' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value && !getFieldValue('mobileNumber')) {
                                                return Promise.reject('At least one of mobile or email is required.');
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <Input placeholder="email@example.com" />
                            </Form.Item>
                        </Col>

                        {scopeValue === 'site' && (
                            <Col span={24}>
                                <Form.Item
                                    label="Assigned Properties"
                                    name="propertyIds"
                                    rules={[
                                        {
                                            validator(_, value) {
                                                if (!value || value.length === 0) {
                                                    return Promise.reject('At least one property required for site scope.');
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder="Select properties"
                                        options={propertyOptions}
                                        showSearch
                                        filterOption={(input, opt) =>
                                            opt?.label?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                </Form.Item>
                            </Col>
                        )}

                        {scopeValue === 'global' && (
                            <Col span={24}>
                                <Alert
                                    type="info"
                                    showIcon
                                    message="Global scope: all property links will be removed by the backend."
                                    style={{ marginBottom: 12 }}
                                />
                            </Col>
                        )}

                        <Col span={12}>
                            <Form.Item label="Active" name="isActive" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '8px 0 16px' }} />

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <Button
                            onClick={closeModal}
                            style={{ height: '38px', borderRadius: '6px', fontWeight: 500 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={saveMutation.isLoading}
                            style={{
                                backgroundColor: colorPrimary,
                                border: 'none',
                                height: '38px',
                                borderRadius: '6px',
                                fontWeight: 500,
                                color: '#fff',
                                boxShadow: 'none',
                            }}
                        >
                            {modalState.contact ? 'Save Changes' : 'Create Contact'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default ContactDirectoryPage;
